import React, { useState } from 'react';
import { ArrowRight, Check, Loader2 } from 'lucide-react';
import { trackLead, trackEvent, getUTMs } from '../services/analytics';

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

// The landing page calls the existing botibonga-studio backend for email.
// Localhost dev hits the local Flask server; everything else hits production.
const LEAD_ENDPOINT = (() => {
  if (typeof window !== 'undefined' && window.location.hostname === 'localhost') {
    return 'http://localhost:5000/public/lead';
  }
  return 'https://api.botibonga.com/public/lead';
})();

export default function LeadCaptureForm({
  source = 'unknown',
  ctaLabel = 'Get Started Free',
  variant = 'default', // 'default' | 'compact'
  pixelEvent = null, // optional campaign-specific event (e.g. 'LeadSalons')
  showMessage = false, // when true, render a "what are you looking for?" textarea
}) {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [message, setMessage] = useState('');
  const [website, setWebsite] = useState(''); // honeypot
  const [status, setStatus] = useState('idle'); // 'idle' | 'submitting' | 'success' | 'error'
  const [errorMsg, setErrorMsg] = useState('');

  const validate = () => {
    if (!name.trim()) return 'Please enter your name.';
    if (!EMAIL_RE.test(email.trim())) return 'Please enter a valid email.';
    if (!phone.trim()) return 'Please enter your WhatsApp number.';
    return null;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (status === 'submitting') return;

    const validationError = validate();
    if (validationError) {
      setErrorMsg(validationError);
      setStatus('error');
      return;
    }

    setStatus('submitting');
    setErrorMsg('');

    try {
      const response = await fetch(LEAD_ENDPOINT, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: name.trim(),
          email: email.trim(),
          phone: phone.trim(),
          message: message.trim(),
          website,
          source,
          utms: getUTMs(),
        }),
      });

      const data = await response.json().catch(() => ({}));
      if (!response.ok || data.success === false) {
        throw new Error(data.error || 'Submission failed');
      }

      trackLead({ source });
      if (pixelEvent) {
        trackEvent(pixelEvent, { source });
      }
      setStatus('success');
      setName('');
      setEmail('');
      setPhone('');
      setMessage('');
    } catch (err) {
      setErrorMsg('Something went wrong. Please try again or email sales@botibonga.com.');
      setStatus('error');
    }
  };

  if (status === 'success') {
    return (
      <div
        role="status"
        aria-live="polite"
        className="rounded-2xl p-6 text-center"
        style={{
          background: 'var(--bg-card)',
          border: '1px solid var(--border-card)',
          boxShadow: 'var(--shadow-card)',
        }}
      >
        <div
          className="inline-flex items-center justify-center w-12 h-12 rounded-full mb-3"
          style={{ background: 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)' }}
        >
          <Check className="w-6 h-6 text-white" />
        </div>
        <h3 className="text-base font-semibold mb-1" style={{ color: 'var(--text-heading)' }}>
          Thanks. We'll be in touch within 24 hours.
        </h3>
        <p className="text-xs" style={{ color: 'var(--text-body)' }}>
          Want to try it now? Sign up free at{' '}
          <a
            href="https://app.botibonga.com"
            className="underline"
            style={{ color: 'var(--text-accent)' }}
          >
            app.botibonga.com
          </a>
          .
        </p>
      </div>
    );
  }

  const inputStyle = {
    background: 'var(--bg-input, var(--bg-card))',
    border: '1px solid var(--border-input, var(--border-card))',
    color: 'var(--text-heading)',
  };

  const labelStyle = { color: 'var(--text-muted)' };

  return (
    <form
      onSubmit={handleSubmit}
      noValidate
      className={`w-full ${variant === 'compact' ? 'max-w-md' : 'max-w-lg'} mx-auto rounded-2xl p-5 sm:p-6 text-left`}
      style={{
        background: 'var(--bg-card)',
        border: '1px solid var(--border-card)',
        boxShadow: 'var(--shadow-card)',
      }}
    >
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <div>
          <label htmlFor={`lf-name-${source}`} className="block text-xs font-medium mb-1.5" style={labelStyle}>
            Name
          </label>
          <input
            id={`lf-name-${source}`}
            type="text"
            autoComplete="name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            className="w-full px-3 py-2.5 rounded-lg text-base sm:text-sm outline-none focus:ring-2 focus:ring-indigo-300"
            style={inputStyle}
            placeholder="Your name"
          />
        </div>
        <div>
          <label htmlFor={`lf-email-${source}`} className="block text-xs font-medium mb-1.5" style={labelStyle}>
            Work email
          </label>
          <input
            id={`lf-email-${source}`}
            type="email"
            autoComplete="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            className="w-full px-3 py-2.5 rounded-lg text-base sm:text-sm outline-none focus:ring-2 focus:ring-indigo-300"
            style={inputStyle}
            placeholder="you@company.com"
          />
        </div>
      </div>

      <div className="mt-3">
        <label htmlFor={`lf-phone-${source}`} className="block text-xs font-medium mb-1.5" style={labelStyle}>
          WhatsApp number
        </label>
        <input
          id={`lf-phone-${source}`}
          type="tel"
          autoComplete="tel"
          value={phone}
          onChange={(e) => setPhone(e.target.value)}
          className="w-full px-3 py-2.5 rounded-lg text-base sm:text-sm outline-none focus:ring-2 focus:ring-indigo-300"
          style={inputStyle}
          placeholder="+254 7..."
        />
      </div>

      {showMessage && (
        <div className="mt-3">
          <label htmlFor={`lf-message-${source}`} className="block text-xs font-medium mb-1.5" style={labelStyle}>
            What kind of solution are you looking for?
          </label>
          <textarea
            id={`lf-message-${source}`}
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            rows={4}
            maxLength={2000}
            className="w-full px-3 py-2.5 rounded-lg text-base sm:text-sm outline-none focus:ring-2 focus:ring-indigo-300 resize-y"
            style={inputStyle}
            placeholder="Tell us about your business and what you'd like the AI assistant to handle (booking, lead capture, FAQs, etc.)"
          />
        </div>
      )}

      {/* Honeypot — visually hidden but available to bots */}
      <div aria-hidden="true" style={{ position: 'absolute', left: '-9999px', top: 'auto', width: 1, height: 1, overflow: 'hidden' }}>
        <label>
          Website
          <input
            type="text"
            tabIndex={-1}
            autoComplete="off"
            value={website}
            onChange={(e) => setWebsite(e.target.value)}
          />
        </label>
      </div>

      {status === 'error' && errorMsg && (
        <p
          role="alert"
          className="mt-3 text-xs px-3 py-2 rounded-lg"
          style={{ background: 'rgba(239, 68, 68, 0.1)', color: '#dc2626' }}
        >
          {errorMsg}
        </p>
      )}

      <button
        type="submit"
        disabled={status === 'submitting'}
        className="mt-4 w-full inline-flex items-center justify-center gap-2 px-6 py-3 rounded-xl font-semibold text-white transition-all duration-300 hover:scale-[1.01] active:scale-[0.99] disabled:opacity-60 disabled:cursor-not-allowed"
        style={{
          background: 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)',
          boxShadow: 'var(--shadow-btn)',
        }}
      >
        {status === 'submitting' ? (
          <>
            <Loader2 className="w-4 h-4 animate-spin" />
            Sending...
          </>
        ) : (
          <>
            {ctaLabel}
            <ArrowRight className="w-4 h-4" />
          </>
        )}
      </button>

      <p className="mt-3 text-[11px] leading-relaxed text-center" style={{ color: 'var(--text-muted)' }}>
        No credit card required · We'll respond within 24 hours
      </p>
    </form>
  );
}
